import { OnSuccessCallback, useCustomMutation } from './index';

import { QueryError, QueryKeys } from '../queries';
import { Assignment, BaseCreateRole, BaseRole } from '../queries/roles';

import Config from '../../config';
import { METHODS } from '../constants';
import { RowMetadata } from '../gridEdit/useEditableGridLogic';
import { ALL_ASSIGNMENTS_CODE } from '../../userSettings/constants';

type BaseWithPrivilegeList<BaseType> = BaseType & { privilegeList: string[] }

type RoleVariableRest<BaseType> = {
  roleAssignments: Assignment[],
  meta: RowMetadata<BaseWithPrivilegeList<BaseType>>
}

type BaseRoleVariable = BaseRole & RoleVariableRest<BaseRole>;

type CreateRoleVariable = BaseCreateRole & RoleVariableRest<BaseCreateRole>

const mapAssignments = (assignments: Assignment[]) => assignments.map(assignment =>
  assignment.assignmentCode !== ALL_ASSIGNMENTS_CODE
    ? assignment
    : {
      ...assignment,
      assignmentCode: 'ALL',
    });

export const useAddRole = (onSuccess: OnSuccessCallback<Response, CreateRoleVariable>) =>
  useCustomMutation<Response, QueryError, CreateRoleVariable>({
    handleData: role => ({
      url: Config.endpoints.INSERT_ROLE,
      method: METHODS.POST,
      body: {
        ...role,
        roleAssignments: mapAssignments(role.roleAssignments),
      },
    }),
    invalidateKey: QueryKeys.roles.RolesList,
    errorMessage: 'Creating Role failed',
    successMessage: 'Role created successfully',
    onSuccess,
  });

export const useUpdateRole = (onSuccess: OnSuccessCallback<Response, BaseRoleVariable>) =>
  useCustomMutation<Response, QueryError, BaseRoleVariable>({
    handleData: role => ({
      url: Config.endpoints.UPDATE_ROLE
        .replace(':roleId', `${role.roleId}`),
      method: METHODS.PUT,
      body: {
        ...role,
        roleAssignments: mapAssignments(role.roleAssignments),
      },
    }),
    invalidateKey: QueryKeys.roles.RolesList,
    errorMessage: 'Updating Role failed',
    successMessage: 'Role updated successfully',
    onSuccess,
  });

export const useDeleteRole = () =>
  useCustomMutation<Response, QueryError, string>({
    handleData: roleId => ({
      url: Config.endpoints.DELETE_ROLE
        .replace(':roleId', roleId),
      method: METHODS.DELETE,
    }),
    invalidateKey: QueryKeys.roles.RolesList,
    errorMessage: 'Removing Role failed',
    successMessage: 'Role removed successfully',
  });
